/**
 * MKS-134 — board filter predicates for the project board (KanbanBoardPage).
 *
 * Pure client-side filtering over the already-authorized board response:
 * kind-aware composite assignee keys ('u:19' / 'e:5' / 'unassigned'), status
 * keys ('s:10' / 'triage'), milestone matching ('m:4' / 'none') and
 * local-day-precision date filters (before / on / after).
 *
 * The composite assignee key IS the MKS-134 bugfix: the pre-MKS-134 board
 * filter read only `assigneeId` (a `number | null` with sentinel 0 for
 * "Unassigned"), so a task assigned to an EXTERNAL user — which has
 * `assigneeId: null` + `externalAssigneeId: <id>` — never produced a filter
 * option and was wrongly matched by "Unassigned".
 *
 * Filtering here is cosmetic (threat T-134-01): it only hides rows the user
 * is already authorized to see and must never be treated as an authorization
 * boundary.
 *
 * MKS-134 (external surface): these same predicates also serve the external
 * portal board/list (`/external/projects/:id/board`). They run over the
 * response `projectService.getBoard` already scoped for that audience (the
 * `where: { OR: [{ isExternal: true }, { externalAssigneeId: actor.id }] }`
 * clause) — so client-side filtering stays cosmetic there too and is never an
 * authorization boundary for the external surface either.
 */
import type { BoardColumn, TaskCard, TaskPriority } from '../services/api/board';
import { addDaysToDayKey, localDayKey, taskDueState } from './taskDueDate';

export type DateFilterMode = 'before' | 'on' | 'after';

/**
 * MKS-341 — the `Due:` slot's three values. Deliberately NOT a `DateFilter`
 * (the created/updated slots above): those answer "which exact day", this one
 * answers "how urgent", which is the question a board reader actually has.
 *
 * - `overdue`   — the due date has passed. Terminal tickets are excluded, the
 *                 same way they are excluded from the card's red chip:
 *                 "terminal tickets are never marked overdue" has to hold for
 *                 the filter too, or the two surfaces disagree about the same
 *                 ticket.
 * - `this_week` — due from today through the end of the current ISO week
 *                 (Sunday), INCLUDING anything already overdue: a reader
 *                 asking "what is due this week" wants last Friday's miss in
 *                 the list, not hidden behind a second filter.
 * - `none`      — no due date at all. The escape hatch for finding the
 *                 tickets nobody has dated yet.
 */
export type DueFilterMode = 'overdue' | 'this_week' | 'none';

/**
 * `date` is a YYYY-MM-DD local day — the raw value of an `<input type="date">`.
 * An empty string means "not yet picked", i.e. the filter slot is inactive.
 */
export interface DateFilter {
  mode: DateFilterMode;
  date: string;
}

export interface BoardFilterState {
  assignee: string | null;
  status: string | null;
  milestone: string | null;
  created: DateFilter | null;
  updated: DateFilter | null;
  /**
   * MKS-163 — multi-select priority slot. Empty array = inactive (show all);
   * a non-empty array ORs the selected priorities together. Unlike the
   * other slots (single-value or null), this one is a set — selecting
   * several priorities widens the match, it does not narrow it further.
   */
  priority: TaskPriority[];
  /**
   * MKS-164 — multi-select label slot. Empty array = inactive (show all); a
   * non-empty array ORs the selected label ids together (a task matches if
   * it carries ANY selected label) — mirrors the `priority` slot's set
   * semantics, not the single-value slots above.
   */
  labels: number[];
  /**
   * MKS-341 — single-value due slot (`null` = inactive). Single-value rather
   * than the priority/label set semantics: the three modes are not a
   * meaningful union — "overdue OR none" describes no reader's question.
   */
  due: DueFilterMode | null;
  /**
   * MKS-419 — merged parent + subprojects board only. A `p:<projectId>` key
   * narrowing the board to ONE project's cards; the id may be the board's own
   * (parent) project as well as a subproject. `null` = inactive. Frontend-only,
   * like `due`: the server querystring is not extended. The page hands the
   * filter this slot only while the merged view is on (and nulls it otherwise),
   * so a stale `?project=` can never narrow a plain board.
   */
  subproject: string | null;
  /**
   * MKS-510 — "which tickets did <person> file". A kind-aware composite key
   * ('u:19' / 'e:5' / 'no_creator'), exactly like `assignee` above, because
   * creator attribution is the same dual-kind (internal user / external user
   * / nobody) shape. `null` = inactive.
   *
   * Frontend-only, like `due` and `subproject`: the server querystring is not
   * extended, the board response already carries the attribution on every
   * card, and the slot is inert while it is `null` — which is how the
   * external portal board (sharing these predicates) and a flag-off project
   * board keep behaving exactly as before.
   */
  creator: string | null;
}

/**
 * All-inactive filter state. Frozen by convention — never mutate it; callers
 * spread it (`{ ...EMPTY_BOARD_FILTERS }`) or replace whole slots.
 */
export const EMPTY_BOARD_FILTERS: BoardFilterState = {
  assignee: null,
  status: null,
  milestone: null,
  created: null,
  updated: null,
  priority: [],
  labels: [],
  due: null, // MKS-341
  subproject: null, // MKS-419
  creator: null, // MKS-510
};

export interface BoardAssigneeOption {
  key: string;
  name: string;
  kind: 'user' | 'external_user' | 'unassigned';
}

/**
 * Composite, kind-aware assignee key for a task.
 *
 * The external branch is checked FIRST on purpose: on an external-assigned
 * task `assigneeId` is null, and the pre-MKS-134 code read only `assigneeId`
 * — which is exactly the bug this key fixes. Kind-less fallbacks keep legacy
 * fixtures (created before assigneeKind existed on the wire type) resolving.
 */
export function assigneeFilterKey(
  task: Pick<TaskCard, 'assigneeId' | 'externalAssigneeId' | 'assigneeKind'>,
): string {
  if (task.assigneeKind === 'external_user' && task.externalAssigneeId != null) {
    return `e:${task.externalAssigneeId}`;
  }
  if (task.assigneeKind === 'user' && task.assigneeId != null) {
    return `u:${task.assigneeId}`;
  }
  // Kind-less fallbacks (legacy fixtures without assigneeKind).
  if (task.externalAssigneeId != null) return `e:${task.externalAssigneeId}`;
  if (task.assigneeId != null) return `u:${task.assigneeId}`;
  return 'unassigned';
}

/**
 * Status key for a board column. The synthetic Triage column has
 * taskStateId=null; a null state id on a non-triage column is treated as
 * triage defensively (taskStateId must never be used as a sentinel — see
 * board.ts INTAKE-02 notes).
 */
export function statusFilterKey(column: Pick<BoardColumn, 'taskStateId' | 'isTriage'>): string {
  if (column.isTriage || column.taskStateId == null) return 'triage';
  return `s:${column.taskStateId}`;
}

/**
 * One option per distinct composite assignee key across ALL columns (triage
 * included — this is a deliberate widening vs. the pre-MKS-134 collector).
 * Sorted by name; the 'unassigned' option is prepended when at least one
 * task has no assignee.
 */
export function collectBoardAssignees(columns: BoardColumn[]): BoardAssigneeOption[] {
  const byKey = new Map<string, BoardAssigneeOption>();
  let hasUnassigned = false;
  for (const col of columns) {
    for (const task of col.tasks) {
      const key = assigneeFilterKey(task);
      if (key === 'unassigned') {
        hasUnassigned = true;
        continue;
      }
      if (byKey.has(key)) continue;
      const kind: BoardAssigneeOption['kind'] = key.startsWith('e:') ? 'external_user' : 'user';
      const id = key.slice(2);
      const name =
        task.assigneeName != null
          ? task.assigneeName
          : kind === 'external_user'
            ? `External ${id}`
            : `User ${id}`;
      byKey.set(key, { key, name, kind });
    }
  }
  const options = [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name));
  if (hasUnassigned) {
    options.unshift({ key: 'unassigned', name: 'Unassigned', kind: 'unassigned' });
  }
  return options;
}

/**
 * MKS-510 — the sentinel key for a card carrying no creator stamp at all
 * (legacy rows pre-date the stamp and keep both id columns NULL). Mirrors
 * `assigneeFilterKey`'s 'unassigned' precedent: such cards get their OWN
 * option rather than being swept into somebody else's.
 */
export const NO_CREATOR_KEY = 'no_creator';

export interface BoardCreatorOption {
  key: string;
  name: string;
  kind: 'user' | 'external_user' | 'no_creator';
}

/**
 * MKS-510 — composite, kind-aware creator key for a task, built the same way
 * as `assigneeFilterKey` (external branch first, then kind-less fallbacks for
 * legacy fixtures that pre-date `createdByKind` on the wire type).
 *
 * NEVER keyed on `createdByName`: two people can share a name, and the whole
 * point of the composite key is that an internal and an external person with
 * id 5 — or with the same name — stay distinct.
 */
export function creatorFilterKey(
  task: Pick<TaskCard, 'createdById' | 'createdByExternalId' | 'createdByKind'>,
): string {
  if (task.createdByKind === 'external_user' && task.createdByExternalId != null) {
    return `e:${task.createdByExternalId}`;
  }
  if (task.createdByKind === 'user' && task.createdById != null) {
    return `u:${task.createdById}`;
  }
  // Kind-less fallbacks (legacy fixtures without createdByKind).
  if (task.createdByExternalId != null) return `e:${task.createdByExternalId}`;
  if (task.createdById != null) return `u:${task.createdById}`;
  return NO_CREATOR_KEY;
}

/**
 * MKS-510 — one option per distinct composite creator key across ALL columns
 * (triage included, like collectBoardAssignees). Sorted by name; the
 * no-creator option is prepended ONLY when a card without a creator stamp is
 * actually on the board, so a fully-stamped board never offers a dead option.
 */
export function collectBoardCreators(columns: BoardColumn[]): BoardCreatorOption[] {
  const byKey = new Map<string, BoardCreatorOption>();
  let hasNoCreator = false;
  for (const col of columns) {
    for (const task of col.tasks) {
      const key = creatorFilterKey(task);
      if (key === NO_CREATOR_KEY) {
        hasNoCreator = true;
        continue;
      }
      if (byKey.has(key)) continue;
      const kind: BoardCreatorOption['kind'] = key.startsWith('e:') ? 'external_user' : 'user';
      const id = key.slice(2);
      const name =
        task.createdByName != null && task.createdByName !== ''
          ? task.createdByName
          : kind === 'external_user'
            ? `External ${id}`
            : `User ${id}`;
      byKey.set(key, { key, name, kind });
    }
  }
  const options = [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name));
  if (hasNoCreator) {
    options.unshift({ key: NO_CREATOR_KEY, name: 'No creator', kind: 'no_creator' });
  }
  return options;
}

export interface BoardLabelOption {
  id: number;
  name: string;
  color: string;
}

/**
 * MKS-164 — one deduplicated option per label present on the loaded board
 * (triage included, mirroring collectBoardAssignees), sorted by name.
 */
export function collectBoardLabels(columns: BoardColumn[]): BoardLabelOption[] {
  const byId = new Map<number, BoardLabelOption>();
  for (const col of columns) {
    for (const task of col.tasks) {
      for (const label of task.labels ?? []) {
        if (!byId.has(label.id)) {
          byId.set(label.id, { id: label.id, name: label.name, color: label.color });
        }
      }
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Local-time YYYY-MM-DD day key for an ISO timestamp, or null when the input
 * is nullish/empty/unparseable. Uses LOCAL getters (not the UTC ones) — the
 * D-08 contract: the user filters by the calendar day they see on the card.
 */
export function toLocalDayKey(iso: string | null | undefined): string | null {
  if (iso == null || iso === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * True when the filter is inactive (null or blank date); otherwise compares
 * the timestamp's local day key to the picked day per the filter mode. A
 * missing or unparseable timestamp never matches an active filter.
 */
export function matchesDateFilter(
  iso: string | null | undefined,
  filter: DateFilter | null,
): boolean {
  if (filter == null || filter.date === '') return true;
  const dayKey = toLocalDayKey(iso);
  if (dayKey == null) return false;
  // Lexicographic comparison of zero-padded YYYY-MM-DD keys is chronological.
  switch (filter.mode) {
    case 'before':
      return dayKey < filter.date;
    case 'on':
      return dayKey === filter.date;
    case 'after':
      return dayKey > filter.date;
  }
}

/**
 * MKS-341 — does a task match an active `Due:` slot?
 *
 * `isTerminal` is NOT available on `TaskCard` (terminality is a property of
 * the COLUMN the card sits in, exactly like `filters.status`), so the caller
 * supplies it — `filterBoardColumns` reads it off the column it is walking.
 * Omitted means "treat as in-progress", which matches `TaskCard`'s own
 * `isTerminal` prop contract.
 *
 * `overdue` deliberately returns false for a terminal ticket: finished work
 * does not run late, and a Done column answering an "overdue" filter is the
 * same bug as a Done column going red.
 */
export function taskMatchesDueFilter(
  task: Pick<TaskCard, 'dueDate'>,
  due: DueFilterMode | null,
  options: { isTerminal?: boolean | undefined; now?: Date | undefined } = {},
): boolean {
  if (due === null) return true;
  const dueDate = task.dueDate ?? null;

  if (due === 'none') return dueDate === null;
  // Both remaining modes are about a real date, so an undated ticket is out.
  if (dueDate === null) return false;

  const now = options.now ?? new Date();
  const state = taskDueState(dueDate, { isTerminal: options.isTerminal, now });
  // A malformed wire value yields no state — never match it into a warning
  // bucket on the strength of an unparseable string.
  if (state === null) return false;

  if (due === 'overdue') return state === 'overdue';

  // `this_week`: today through the end of the current ISO week (Sunday),
  // plus everything already overdue. Computed in day-string space like the
  // rest of this module, so it needs no Date arithmetic on the due value.
  const today = localDayKey(now);
  const isoDay = now.getDay() === 0 ? 7 : now.getDay(); // Sunday = 7, ISO-style
  const endOfWeek = addDaysToDayKey(today, 7 - isoDay);
  return dueDate <= endOfWeek;
}

/**
 * Task-level filter slots only: assignee, creator, milestone, created, updated.
 * Deliberately does NOT evaluate `filters.status` — status is a property of
 * the COLUMN a task sits in, and is applied by filterBoardColumns.
 */
export function taskMatchesBoardFilters(task: TaskCard, filters: BoardFilterState): boolean {
  if (filters.assignee != null && assigneeFilterKey(task) !== filters.assignee) {
    return false;
  }
  // MKS-510 — single-value, exact composite-key match, like `assignee` above.
  // A key that matches no card on the board simply empties it; there is no
  // NaN/prefix coercion to fall through (the key is compared as a string).
  if (filters.creator != null && creatorFilterKey(task) !== filters.creator) {
    return false;
  }
  if (filters.milestone != null) {
    if (filters.milestone === 'none') {
      if (task.milestoneId != null) return false;
    } else {
      const parsed = Number(filters.milestone.slice(2));
      // No NaN pass-through (threat T-134-03): a crafted key matches nothing.
      if (!Number.isFinite(parsed)) return false;
      if (task.milestoneId !== parsed) return false;
    }
  }
  if (filters.subproject != null) {
    const parsed = Number(filters.subproject.slice(2));
    // No NaN pass-through: a malformed key matches nothing.
    if (!filters.subproject.startsWith('p:') || !Number.isFinite(parsed)) return false;
    if (task.projectId !== parsed) return false;
  }
  if (!matchesDateFilter(task.createdAt, filters.created)) return false;
  if (!matchesDateFilter(task.lastActivityAt ?? null, filters.updated)) return false;
  // MKS-163 — an inactive (empty) selection matches everything; a non-empty
  // selection ORs the chosen priorities. Server column is NOT NULL DEFAULT
  // 'normal', so a missing wire value (legacy fixture) is treated as 'normal'.
  if (filters.priority.length > 0 && !filters.priority.includes(task.priority ?? 'normal')) {
    return false;
  }
  // MKS-164 — an inactive (empty) selection matches everything; a non-empty
  // selection matches a task carrying ANY selected label (OR, not AND). A
  // task with no labels never matches an active selection.
  if (filters.labels.length > 0) {
    const taskLabelIds = (task.labels ?? []).map((l) => l.id);
    if (!filters.labels.some((id) => taskLabelIds.includes(id))) return false;
  }
  return true;
}

/** Number of active filter slots (a date slot with a blank date is inactive). */
export function countActiveBoardFilters(filters: BoardFilterState): number {
  let count = 0;
  if (filters.assignee != null) count += 1;
  if (filters.status != null) count += 1;
  if (filters.milestone != null) count += 1;
  if (filters.created != null && filters.created.date !== '') count += 1;
  if (filters.updated != null && filters.updated.date !== '') count += 1;
  // MKS-163 — counted as ONE active slot regardless of how many priorities
  // are selected (mirrors the single-value slots above: "is this filter
  // dimension active", not "how many values are in it").
  if (filters.priority.length > 0) count += 1;
  // MKS-164 — counted as ONE active slot regardless of how many labels are
  // selected, mirroring the priority slot's precedent immediately above:
  // "is this filter dimension active", not "how many values are in it".
  if (filters.labels.length > 0) count += 1;
  // MKS-341 — one slot, single-value, same as assignee/status/milestone above.
  if (filters.due !== null) count += 1;
  // MKS-419 — one slot.
  if (filters.subproject !== null) count += 1;
  // MKS-510 — one slot, single-value, same as assignee above.
  if (filters.creator !== null) count += 1;
  return count;
}

/**
 * Narrows each column's tasks to those matching the active filters. Never
 * drops a column (D-04) — empty columns must stay visible so dnd-kit drop
 * targets survive; the status filter therefore EMPTIES non-matching columns
 * rather than removing them. Returns the SAME array reference when no filter
 * is active (D-05) so consuming useMemos keep downstream props referentially
 * stable.
 */
export function filterBoardColumns(
  columns: BoardColumn[],
  filters: BoardFilterState,
  now?: Date,
): BoardColumn[] {
  if (countActiveBoardFilters(filters) === 0) return columns;
  return columns.map((col) => {
    const statusMatches = filters.status === null || statusFilterKey(col) === filters.status;
    return {
      ...col,
      tasks: statusMatches
        ? col.tasks.filter(
            (t) =>
              taskMatchesBoardFilters(t, filters) &&
              // MKS-341 — evaluated HERE rather than inside
              // taskMatchesBoardFilters for the same reason `filters.status`
              // is: it needs the COLUMN's `isTerminal`, which a task-level
              // predicate cannot see.
              taskMatchesDueFilter(t, filters.due, { isTerminal: col.isTerminal, now }),
          )
        : [],
    };
  });
}
